import FixtureDetailPanel from '../FixtureDetailPanel'
import FixtureGoals from '../FixtureGoals'
import { displayMinute } from '../../app/matchClock'
import { getClubBanner, getClubAbbr } from '../../clubBanners'
import { standingTierFor, type PickTier } from './pickTier'
import type { FixtureView, MatchEvent } from '../../types'

/**
 * One fixture's collapsed row and everything that renders inside it, lifted out of FixturesPage
 * so the page itself stays under the 500-line bar.
 */

function formatKickoffTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}


// Fixed-width, always two lines, so the rail never shifts regardless of
// state (mock: .rail). Never touches the result-colour tiers — a live
// fixture structurally cannot pick one up here.
function CenterRail({ view, liveElapsedMs }: { view: FixtureView; liveElapsedMs: number }) {
  if (view.status === 'live') {
    const score = view.liveScore
    return (
      <div className="fixtures-page__rail">
        <span className="fixtures-page__rail-primary fixtures-page__rail-score fixtures-page__rail-score--live">
          {score ? `${score.homeScore}–${score.awayScore}` : '–'}
        </span>
        <span className="fixtures-page__rail-secondary fixtures-page__rail-live">
          <span className="fixtures-page__live-dot" aria-hidden="true" />
          {/* Off the one shared clock when the server is the source: "HT", a
              ticking minute, or "—" rather than a fabricated number. The mock sim keeps the plain
              number it has always shown. */}
          {view.liveStatus === null
            ? `${view.liveMinute ?? 0}'`
            : displayMinute(view.liveStatus, view.liveMinute, liveElapsedMs)}
        </span>
      </div>
    )
  }
  if (view.status === 'final') {
    const score = view.finalScore
    return (
      <div className="fixtures-page__rail">
        <span className="fixtures-page__rail-primary fixtures-page__rail-score">
          {score ? `${score.homeScore}–${score.awayScore}` : '–'}
        </span>
        <span className="fixtures-page__rail-secondary">FT</span>
      </div>
    )
  }
  return (
    <div className="fixtures-page__rail">
      <span className="fixtures-page__rail-primary fixtures-page__rail-time">
        {formatKickoffTime(view.kickoff)}
      </span>
      <span className="fixtures-page__rail-secondary fixtures-page__rail-dim">&mdash;</span>
    </div>
  )
}

// Result palette applied to a played (final) fixture's pick line so it
// communicates whether the pick was right — the same 5/3/1/0 → exact/correct/
// partial/miss tiering GameCard's result cards use. A live
// fixture stays neutral: live isn't a reveal, same rule as the center rail.
function outcomeTierFor(view: FixtureView): PickTier | null {
  return view.status === 'final' ? standingTierFor(view) : null
}

// View-only — no tap target, no CTA. Only rendered for live/finished
// fixtures; upcoming rows show the fixture alone (all predicting happens on
// the Predict page). "Set + live" stays neutral — same live-isn't-a-reveal
// rule as the center rail.
function PredictionLine({ view }: { view: FixtureView }) {
  if (!view.prediction) {
    return (
      <div className="fixtures-page__pick fixtures-page__pick--none">
        <span className="fixtures-page__pick-label label-eyebrow">No prediction</span>
      </div>
    )
  }

  const tier = outcomeTierFor(view)

  return (
    <div className={`fixtures-page__pick${tier ? ` fixtures-page__pick--${tier}` : ''}`}>
      <span className="fixtures-page__pick-label label-eyebrow">Your pick</span>
      <span className="fixtures-page__pick-score">
        {view.prediction.homeScore}&ndash;{view.prediction.awayScore}
      </span>
    </div>
  )
}

/**
 * A sending-off, marked against the team that suffered it: the same 10×14 red chip
 * the timeline uses, on the collapsed row, so "they're down to ten" is visible without opening
 * anything. Two reds render two chips — a count would need a legend.
 */
function RedCards({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <span className="fixtures-page__reds" aria-label={`${count} red card${count > 1 ? 's' : ''}`}>
      {Array.from({ length: count }, (_, index) => (
        <span key={index} className="fixtures-page__red-card" />
      ))}
    </span>
  )
}

interface FixtureRowProps {
  view: FixtureView
  /** Set only on a fixture that has kicked off — the only one with a timeline to show. */
  onToggle?: () => void
  expanded: boolean
  /** Sendings-off per side, from the gameweek's one events request. */
  redCards?: { home: number; away: number }
  /**
   * This fixture's goals, from the same request. Shown on the collapsed row so the
   * scorers are readable without opening anything.
   */
  goals?: MatchEvent[]
  /** Milliseconds since the live poll landed, ticking. Anchored once for the whole page. */
  liveElapsedMs: number
  /** Registers the row element by fixture id so the page can scroll to it. */
  rowRef?: (el: HTMLDivElement | null) => void
  /**
   * Plays the one-shot arrival wash in this tier's colour — set only on the row the
   * user was sent to, and `'none'` for a fixture they never predicted. Absent means no wash, which
   * is also the reduced-motion path.
   */
  washTier?: PickTier | 'none'
}

export function FixtureRow({
  view,
  onToggle,
  expanded,
  redCards,
  goals,
  liveElapsedMs,
  rowRef,
  washTier,
}: FixtureRowProps) {
  const panelId = `fixture-detail-${view.id}`
  const body = (
    <>
      <div className="fixtures-page__fixture">
        <div className="fixtures-page__side fixtures-page__side--home">
          <RedCards count={redCards?.home ?? 0} />
          <span className="fixtures-page__team fixtures-page__team--home" aria-label={view.homeTeam}>
            {getClubAbbr(view.homeTeam)}
          </span>
          {getClubBanner(view.homeTeam) && (
            <img
              className="fixtures-page__shield"
              src={getClubBanner(view.homeTeam)}
              alt=""
              aria-hidden="true"
            />
          )}
        </div>
        <CenterRail view={view} liveElapsedMs={liveElapsedMs} />
        <div className="fixtures-page__side fixtures-page__side--away">
          {getClubBanner(view.awayTeam) && (
            <img
              className="fixtures-page__shield"
              src={getClubBanner(view.awayTeam)}
              alt=""
              aria-hidden="true"
            />
          )}
          <span className="fixtures-page__team fixtures-page__team--away" aria-label={view.awayTeam}>
            {getClubAbbr(view.awayTeam)}
          </span>
          <RedCards count={redCards?.away ?? 0} />
        </div>
      </div>
      {/* The goals, always — readable without opening anything. Hidden once the
          panel is open, which shows the same goals itself among the cards and substitutions. A
          goalless fixture renders nothing at all: no empty state, which is the panel's job. */}
      {!expanded && goals && goals.length > 0 && <FixtureGoals goals={goals} />}
      {(view.status === 'live' || view.status === 'final') && <PredictionLine view={view} />}
    </>
  )

  const className = [
    'fixtures-page__row',
    view.status === 'live' ? 'fixtures-page__row--live' : null,
    washTier ? 'fixtures-page__row--wash' : null,
    washTier ? `fixtures-page__row--wash-${washTier}` : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={className} ref={rowRef}>
      {/* An upcoming fixture has no timeline to open, so it stays a plain row rather than a
          control that does nothing. */}
      {onToggle ? (
        <button
          type="button"
          className="fixtures-page__disclosure"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={panelId}
        >
          {body}
        </button>
      ) : (
        body
      )}
      {expanded && (
        <FixtureDetailPanel
          id={panelId}
          fixtureId={view.id}
          homeTeamId={view.homeTeam}
          awayTeamId={view.awayTeam}
          // The live poll is the heartbeat: this changes when the fixture moves, and the panel
          // refetches off it rather than running a timer of its own. Undefined once the match is
          // over, because a settled timeline cannot change.
          liveVersion={
            view.status === 'live'
              ? `${view.liveMinute}:${view.liveScore?.homeScore ?? ''}-${view.liveScore?.awayScore ?? ''}`
              : undefined
          }
        />
      )}
    </div>
  )
}
